import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { io, Socket } from 'socket.io-client';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class SocketIoService {
  private publicSocket: Socket; // For guest/public channels
  private authenticatedSocket: Socket | null = null; // For logged-in users (private/presence channels)
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.initializePublicSocket();
    }
  }

  // Public channel - used for general announcements
  // Example: channel('deposit-channel_USD')
  channel(channelName: string): any {
    return this.createChannel(channelName, 'public');
  }

  // Private channel (authentication required) - used for user-specific data
  // Example: private('update-operator-channel.123')
  private(channelName: string): any {
    return this.createChannel(channelName, 'private');
  }

  // Presence channel (authentication required + presence awareness) - used for user-online channels.
  // This means can know who has joined, who is present, and who has left the channel.
  // Laravel Echo style chaining: join('deposit-room.456').here().joining().leaving()
  join(channelName: string): any {
    return this.createChannel(channelName, 'presence');
  }

  private getSocketForChannel(channelType: 'private' | 'presence' | 'public'): Socket | null {
    if (!this.isBrowser) return null;

    // If user is logged in (has tokens), use authenticated socket for all channels
    if (this.hasUserTokens()) {
      if (!this.authenticatedSocket) {
        this.initializeAuthenticatedSocket();
      }
      return this.authenticatedSocket!;
    }

    // Private and presence channels require authenticated socket
    if (channelType === 'private' || channelType === 'presence') {
      if (!this.authenticatedSocket) {
        this.initializeAuthenticatedSocket();
      }
      return this.authenticatedSocket!;
    }

    // Public channels for guest users use public socket
    return this.publicSocket;
  }

  private getUserToken(): { access_token?: string; plaintext_token?: string } | null {
    try {
      return JSON.parse(localStorage.getItem('user_token') || '{}');
    } catch {
      return null;
    }
  }

  private hasUserTokens(): boolean {
    const userToken = this.getUserToken();
    return !!(userToken?.access_token && userToken?.plaintext_token);
  }

  private createChannel(channelName: string, channelType: 'private' | 'presence' | 'public'): any {
    const socket = this.getSocketForChannel(channelType);

    if (!socket) {
      // Server-side: return no-op stubs
      const noopChannel: any = {
        listen: () => () => {},
        here: () => noopChannel,
        joining: () => noopChannel,
        leaving: () => noopChannel,
      };
      return noopChannel;
    }

    // Join the channel when connected
    const joinChannel = () => {
      socket.emit('join', {
        channel: channelName,
        channelType: channelType
      });
    };

    // Join immediately if connected, otherwise wait for connection
    if (socket.connected) {
      joinChannel();
    } else {
      socket.once('connect', joinChannel);
    }
    
    // Return different method for presence channels
    if (channelType === 'presence') {
      const channel = {
        // Laravel Echo-style chaining for presence channels
        here: (callback: (users: any[]) => void) => {
          const socket = this.getSocketForChannel('presence');
          socket.on(channelName, (data: any) => {
            if (data.type === 'here') {
              callback(data.users);
            }
          });
          return channel;
        },

        joining: (callback: (user: any) => void) => {
          const socket = this.getSocketForChannel('presence');
          socket.on(channelName, (data: any) => {
            if (data.type === 'joining') {
              callback(data.user);
            }
          });
          return channel;
        },

        leaving: (callback: (user: any) => void) => {
          const socket = this.getSocketForChannel('presence');
          socket.on(channelName, (data: any) => {
            if (data.type === 'leaving') {
              callback(data.user);
            }
          });
          return channel;
        }
      };
      return channel;
    } 

    return {
      listen: (eventName: string, callback: (data: any) => void): () => void => {
        const socket = this.getSocketForChannel(channelType);
        const wrappedCallback = (data: any) => {
          // Only process events for the current channel
          if (data && data._originalChannel === channelName) {
            callback(data);
          }
        };
        socket.on(eventName, wrappedCallback);
        
        // Return unsubscribe function
        return () => {
          socket.off(eventName, wrappedCallback);
        };
      }
    };
  }

  emit(eventName: string, data?: any): void {
    if (!this.isBrowser) return;
    const socket = this.authenticatedSocket || this.publicSocket;
    socket.emit(eventName, data);
  }

  disconnect(): void {
    if (!this.isBrowser) return;
    if (this.publicSocket) {
      this.publicSocket.disconnect();
    }
    if (this.authenticatedSocket) {
      this.authenticatedSocket.disconnect();
      this.authenticatedSocket = null;
    }
  }

  leave(channelName: string): void {
    if (!this.isBrowser) return;
    const socket = this.authenticatedSocket || this.publicSocket;
    if (socket.connected) {
      socket.emit('leave', channelName);
      return;
    }
    socket.once('connect', () => socket.emit('leave', channelName));
  }

  private initializePublicSocket(): void {
    const headers: any = {
      'X-User-Model': 'member',
      'X-Site-Prefix': environment.sitePrefix,
      'X-Api-Base': environment.apiBase,
      'X-Project-Code': environment.wssCode
    };
    
    this.publicSocket = io(environment.wssHost, {
      withCredentials: false,
      extraHeaders: headers
    });
  }

  private initializeAuthenticatedSocket(): void {
    if (this.authenticatedSocket) {
      return;
    }
    
    // Get token from localStorage
    const userToken = this.getUserToken();
    
    if (!userToken || !userToken.access_token || !userToken.plaintext_token) {
      throw new Error('SocketIoService: Cannot initialize authenticated socket without tokens');
    }
    
    const token = {
      access_token: userToken.access_token,
      plaintext_token: userToken.plaintext_token
    };
    
    const headers: any = {
      'X-User-Model': 'member',
      'X-Site-Prefix': environment.sitePrefix,
      'X-Api-Base': environment.apiBase,
      'X-Project-Code': environment.wssCode,
      'access-token': token.access_token,
      'token-selector': token.plaintext_token
    };
    
    this.authenticatedSocket = io(environment.wssHost, {
      withCredentials: false,
      extraHeaders: headers
    });
  }
}
